import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  authenticate,
  getToken,
  reauthenticate,
  updateSyncPair,
} from '~/api/evisync';
import { showToast } from '~/components/Shared/EcToast/showToast';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Button } from '~/eds/src/components/Button/Button';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { QueryParamType } from '~/enums/QueryParamType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { ERROR } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingSpinner from '~/ui/components/LoadingSpinner/LoadingSpinner';
import { removeQueryParam } from '~/utils/browser';

import { resetSyncPairAuthCode } from '../../util';

function Authentication() {
  const { location, navigate } = useRouting();
  const intl = useIntl();
  const dispatch = useDispatch();

  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const { id, state, code, name, provider, tokenId } = activeSyncPair;
  const hasTokenId = !!tokenId;
  const isPairCreated = !!id;
  const isLoadingToken = !!state;

  const authenticateErrorMessage = intl.formatMessage(
    {
      id: 'Authentication.errorMessage',
      defaultMessage: 'Unable to contact {name}, please try again.',
      description:
        'Error message when authentication fails. Example: name = "Google Drive"',
    },
    {
      name,
    },
  );

  useEffect(() => {
    if (!hasTokenId && state && code) {
      const promise = getToken({ provider, state, code });
      dispatch(
        slices.integrations.actions.setActiveSyncPair(
          resetSyncPairAuthCode(activeSyncPair),
        ),
      );

      promise
        .then((response) => {
          const updatedActiveSyncPair = {
            ...activeSyncPair,
            tokenId: response.id,
          };
          dispatch(
            slices.integrations.actions.setActiveSyncPair(
              resetSyncPairAuthCode(updatedActiveSyncPair),
            ),
          );

          if (isPairCreated) {
            executeUpdate();
            handleUpdateNext();
          } else {
            handleCreateNext();
          }

          removeQueryParam(location, navigate, QueryParamType.States);
        })
        .catch(() => {
          showToast(
            ERROR,
            intl.formatMessage(
              {
                id: 'Authentication.authenticationError',
                defaultMessage:
                  'There is an error authenticating with {name}, please try again.',
                description:
                  'Error message when authentication fails. Example: name = "Google Drive"',
              },
              {
                name,
              },
            ),
          );
        });
    }
  }, [activeSyncPair]); // eslint-disable-line react-hooks/exhaustive-deps

  const { isLoading: isAuthenticating, executor: executeAuthenticate } =
    useAsync(
      authenticate,
      { provider, returnTo: window.location.href },
      {
        errorToastMessage: authenticateErrorMessage,
        successHandler: (response) => (window.location.href = response.url),
      },
    );

  const { isLoading: isReAuthenticating, executor: executeReAuthenticate } =
    useAsync(
      reauthenticate,
      { id, returnTo: window.location.href },
      {
        errorToastMessage: authenticateErrorMessage,
        successHandler: (response) => (window.location.href = response.url),
      },
    );

  const { executor: executeUpdate } = useAsync(updateSyncPair, activeSyncPair, {
    errorToastMessage: intl.formatMessage({
      id: 'Authentication.updateErrorMessage',
      defaultMessage: 'Unable to update sync pair, please try again.',
      description: 'Error message when updating sync pair fails',
    }),
    successToastMessage: intl.formatMessage(
      {
        id: 'Authentication.updateSuccessMessage',
        defaultMessage: 'Sync pair updated successfully name: {name}',
        description:
          'Success message when sync pair is updated. Example: name = "Google Drive"',
      },
      { name },
    ),
    successHandler: (response) => {
      dispatch(slices.integrations.actions.setActiveSyncPair(response));
    },
  });

  const handleCreateNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.Folders,
      ),
    );
  };

  const handleUpdateNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.None,
      ),
    );
  };

  if (isLoadingToken) {
    return (
      <FlexLayout alignItems="center" flexDirection="column" space={12}>
        <ProviderLogo provider={provider} />
        <LoadingSpinner />
      </FlexLayout>
    );
  }

  const nextStep = (
    <Button
      id={OnboardingIdType.LoginWithProviderButton}
      isLoading={isAuthenticating || isReAuthenticating}
      text={intl.formatMessage(
        {
          id: 'Authentication.loginButtonText',
          defaultMessage: 'Login with {name}',
          description:
            'Button text to login with provider. Example: name = "Google Drive"',
        },
        {
          name,
        },
      )}
      onClick={isPairCreated ? executeReAuthenticate : executeAuthenticate}
    />
  );

  return (
    <FlexLayout
      alignItems="center"
      flex="auto"
      flexDirection="column"
      space={12}
    >
      <ProviderLogo provider={provider} />
      {hasTokenId ? null : nextStep}
    </FlexLayout>
  );
}

export default Authentication;
